/* eslint-disable react/destructuring-assignment */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import { Button, Input, Form } from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';

import InfoView from 'components/common/InfoView';
import { resetPassword, forgotPassword } from 'redux/actions/Auth';
import logo from '../assets/images/kitegateway_icon_dark.png';
import Scrollbars from 'react-custom-scrollbars';

const FormItem = Form.Item;
const CAPTCHA_SITE_KEY = process.env.REACT_APP_GOOGLE_CAPTCHA_SITE_KEY;

const PasswordResetForm = (props) => {
  const dispatch = useDispatch();
  const userEmail = useSelector(({ auth }) => auth.forgotPasswordMail);
  const { message } = useSelector(({ auth }) => auth.passwordReset);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);
  const captchaReady = !CAPTCHA_SITE_KEY || !!captchaToken;
  const [count, setCount] = useState(60);
  const [readOnly, setReadOnly] = useState(true);

  // A captcha token can only be verified once, so a fresh one is needed after every request
  const resetCaptcha = () => {
    if (captchaRef.current) captchaRef.current.reset();
    setCaptchaToken('');
  };

  const handleSubmitFailure = (errorInfo) => {
    console.error('Error: ', errorInfo);
  };

  const handleSubmit = (values) => {
    dispatch(resetPassword({ ...values, email: userEmail, captcha_token: captchaToken }));
    resetCaptcha();
  };

  useEffect(() => {
    if (message === 'Password reset successfully!') {
      props.history.push('/signin');
    }
  }, [message, props.history]);

  useEffect(() => {
    if (!userEmail) {
      props.history.push('/forgot-password');
    }
  }, [userEmail, props.history]);

  useEffect(() => {
    let start = Number(count);
    const end = Number(0);

    if (start === end) return;

    const incrementTime = 2000;
    const timer = setInterval(() => {
      start -= 2;
      setCount(start);
      if (start === end) clearInterval(timer);
    }, incrementTime);
  }, [count]);

  const handleRegenerateCode = () => {
    dispatch(forgotPassword({ email: userEmail, captcha_token: captchaToken }));
    resetCaptcha();
    setCount(60);
  };

  const enablePasswordField = () => {
    setTimeout(() => setReadOnly(false), 300);
  };

  const disableAutoFill = () => {
    setReadOnly(true);
    enablePasswordField();
  };

  const passwordChanged = (e) => {
    if (e.target.value.trim().length === 0) {
      disableAutoFill();
    }
  };

  return (
    <Scrollbars>
      <div className="gx-login-container">
        <div className="gx-login-content">
          <div className="gx-login-header">
            <img src={logo} alt="Kitegateway" style={{ width: '45%' }} title="Kitegateway" />
          </div>
          <div className="gx-mb-4">
            <h2 className="gx-text-primary">Reset Your Password</h2>
            <p>
              Please enter the
              {' '}
              <b>OTP code</b>
              {' '}
              sent to
              {' '}
              {userEmail}
              {' '}
              and a new password
            </p>
            <p>
              If you haven&apos;t got the code sent to your email,
              you can generate another code
              {(count !== 0) ? ` in ${count} seconds.` : ' now.'}
            </p>
            <div className="password-reset-code">
              <Button
                type="primary"
                onClick={() => handleRegenerateCode()}
                disabled={((count !== 0) || !userEmail || !captchaReady)}
              >
                Resend Code
              </Button>
              <p className="gx-mt-2 gx-mb-0">Complete the captcha below before requesting a new code or submitting.</p>
            </div>
          </div>
          <Form
            layout="vertical"
            name="password-reset-form"
            onFinish={handleSubmit}
            onFinishFailed={handleSubmitFailure}
            className="gx-signin-form gx-form-row0"
            autoComplete="off"
          >
            <FormItem
              rules={[
                { required: true, message: 'OTP is required!' },
              ]}
              label="OTP"
              name="token"
            >
              <Input.Password autoComplete="off" />
            </FormItem>
            <FormItem
              rules={[
                { required: true, message: 'This input is required!' },
              ]}
              label="New Password"
              name="password"
            >
              <Input onChange={passwordChanged} onContextMenu={(e) => e.preventDefault()} onClick={disableAutoFill} readOnly={readOnly} onFocus={enablePasswordField} type="password" placeholder="Password" autoComplete="off" />
            </FormItem>
            <FormItem
              rules={[
                { required: true, message: 'This input is required!' },
              ]}
              label="Confirm Password"
              name="rePassword"
            >
              <Input onChange={passwordChanged} onContextMenu={(e) => e.preventDefault()} onClick={disableAutoFill} readOnly={readOnly} onFocus={enablePasswordField} type="password" placeholder="Password" autoComplete="off" />
            </FormItem>
            <FormItem>
              {CAPTCHA_SITE_KEY && (
                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey={CAPTCHA_SITE_KEY}
                  onChange={setCaptchaToken}
                  onExpired={() => setCaptchaToken('')}
                />
              )}
              <Input autoComplete="username" hidden disabled value={userEmail} />
            </FormItem>
            <FormItem>
              <Button
                type="primary"
                htmlType={captchaReady ? 'submit' : 'button'}
                className={`gx-mb-0 ${captchaReady ? 'enabled' : 'disabled'
                  }`}
              >
                Submit
              </Button>
              <div style={{ display: 'flex', marginTop: '7px', float: 'right' }}>
                <Link to="/signin">
                  Sign In
                  {' '}
                  {'→'}
                </Link>
              </div>
            </FormItem>
          </Form>
        </div>
        <InfoView />
      </div>
    </Scrollbars>
  );
};

PasswordResetForm.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

export default PasswordResetForm;
